/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import AiManagementOcorrenciasTable from 'components/molecules/ai_management_ocorrencias_table';
import { useHiringHistoryDetail } from 'hooks/useHiringHistoryDetail';
import { useParticipantFiles } from 'hooks/useParticipantFiles';
import {
  HiringHistoryDrawerItem,
  HiringHistoryParticipant,
  participantContractsHref,
  participantDocumentsHref,
} from 'services/actions/ai_management_hiring_history';
import AiManagementCompanyDrawer from 'components/organisms/ai_management_company_drawer';
import { count, dateBr, money, percent } from 'services/ai_management_format';
import { Content } from './styles';

interface AiManagementHiringHistoryDrawerProps {
  biddingId: number | null;
  historicBiddingId: number;
  itemKey: string;
  notice: string | null;
  onClose: () => void;
}

const unitPrice = (value: number | null, quantity: number | null) =>
  value === null || !quantity ? null : value / quantity;

const Meta: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className = 'col',
  children,
}) => (
  <div className={`${className} mb-3 min-width-0`}>
    <div className="meta-label">{label}</div>
    <div className="meta-value">{children}</div>
  </div>
);

type OpenCompany = (participant: HiringHistoryParticipant) => void;

const CompanyLink: React.FC<{ participant: HiringHistoryParticipant; onOpen: OpenCompany }> = ({
  participant,
  onOpen,
}) =>
  participant.cnpj ? (
    <button type="button" className="btn btn-link company-link" onClick={() => onOpen(participant)}>
      {participant.participante ?? '—'}
    </button>
  ) : (
    <span className="text-primary">{participant.participante ?? '—'}</span>
  );

type ParticipantFiles = ReturnType<typeof useParticipantFiles>;

const FileLink: React.FC<{ href: string | null }> = ({ href }) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer">
      Download
    </a>
  ) : (
    <span className="text-muted">—</span>
  );

const ParticipantRow: React.FC<{
  participant: HiringHistoryParticipant;
  onOpen: OpenCompany;
  files: ParticipantFiles;
  biddingId: number | null;
  historicBiddingId: number;
}> = ({ participant, onOpen, files, biddingId, historicBiddingId }) => (
  <tr>
    <td>{participant.posicao ?? '—'}</td>
    <td className="participant-name">
      <CompanyLink participant={participant} onOpen={onOpen} />
      {participant.adjudicado ||
      participant.desclassificada ||
      participant.inabilitada ||
      participant.me_epp ? (
        <div className="participant-badges mt-1">
          {participant.adjudicado ? (
            <span className="badge badge-success mr-1">Adjudicada</span>
          ) : null}
          {participant.desclassificada ? (
            <span className="badge badge-warning mr-1">Desclassificada</span>
          ) : null}
          {participant.inabilitada ? (
            <span className="badge badge-warning mr-1">Inabilitada</span>
          ) : null}
          {participant.me_epp ? (
            <span className="badge badge-light border mr-1">ME/EPP</span>
          ) : null}
        </div>
      ) : null}
    </td>
    <td className="text-nowrap">{money(participant.proposta_inicial)}</td>
    <td className="text-nowrap">{money(participant.lance_final)}</td>
    <td className="text-nowrap">{percent(participant.reducao)}</td>
    <td className="text-nowrap text-center">
      <FileLink
        href={
          biddingId && participant.cnpj && files.hasDocuments(participant.cnpj)
            ? participantDocumentsHref(biddingId, historicBiddingId, participant.cnpj)
            : null
        }
      />
    </td>
    <td className="text-nowrap text-center">
      <FileLink
        href={
          biddingId && participant.cnpj && files.hasContracts(participant.cnpj)
            ? participantContractsHref(biddingId, historicBiddingId, participant.cnpj)
            : null
        }
      />
    </td>
  </tr>
);

const PARTICIPANTS_PER_PAGE = 10;

const positionOf = (participant: HiringHistoryParticipant) => {
  const value = Number(participant.posicao);
  return Number.isFinite(value) && participant.posicao !== null ? value : Number.MAX_SAFE_INTEGER;
};

const ParticipantsTable: React.FC<{
  participants: HiringHistoryParticipant[];
  onOpen: OpenCompany;
  files: ParticipantFiles;
  biddingId: number | null;
  historicBiddingId: number;
}> = ({ participants, onOpen, files, biddingId, historicBiddingId }) => {
  const [page, setPage] = useState(1);
  const ordered = [...participants].sort((a, b) => positionOf(a) - positionOf(b));
  const visible = ordered.slice((page - 1) * PARTICIPANTS_PER_PAGE, page * PARTICIPANTS_PER_PAGE);

  useEffect(() => setPage(1), [participants]);

  if (!participants.length) return null;

  return (
    <>
      <div className="table-responsive participants-table mb-3">
        <table className="table bg-white mb-0">
          <thead>
            <tr>
              <th scope="col">Posição</th>
              <th scope="col">Participantes</th>
              <th scope="col">Proposta Inicial</th>
              <th scope="col">Lance Final</th>
              <th scope="col">Redução</th>
              <th scope="col" className="text-center">
                Documentos
              </th>
              <th scope="col" className="text-center">
                Contratos
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((participant) => (
              <ParticipantRow
                key={`${participant.cnpj}-${participant.posicao}`}
                participant={participant}
                onOpen={onOpen}
                files={files}
                biddingId={biddingId}
                historicBiddingId={historicBiddingId}
              />
            ))}
          </tbody>
        </table>
      </div>
      {participants.length > PARTICIPANTS_PER_PAGE ? (
        <div className="d-flex justify-content-end mb-4">
          <Pagination
            postsPerPage={PARTICIPANTS_PER_PAGE}
            totalPosts={participants.length}
            currentPage={page}
            paginate={setPage}
            firstAndLastPage
          />
        </div>
      ) : null}
    </>
  );
};

const RejectionsTable: React.FC<{
  participants: HiringHistoryParticipant[];
  onOpen: OpenCompany;
}> = ({ participants, onOpen }) => {
  const rejected = participants.filter((p) => p.desclassificada || p.inabilitada);
  if (!rejected.length) return null;

  return (
    <div className="participants-table mb-4">
      <div className="rejections-title">Desclassificação / Inabilitação</div>
      <div className="table-responsive">
        <table className="table bg-white mb-0">
          <thead>
            <tr>
              <th scope="col">Empresa</th>
              <th scope="col">Tipo</th>
              <th scope="col">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {rejected.map((participant) => (
              <tr key={`${participant.cnpj}-${participant.posicao}`}>
                <td>
                  <CompanyLink participant={participant} onOpen={onOpen} />
                </td>
                <td className="text-nowrap">
                  <span className="badge rejection-badge">
                    {participant.desclassificada ? 'Desclassificado' : 'Inabilitado'}
                  </span>
                </td>
                <td className="rejection-reason">
                  {participant.motivo ?? 'Não informado no portal'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ItemBlock: React.FC<{
  item: HiringHistoryDrawerItem;
  onOpenCompany: OpenCompany;
  files: ParticipantFiles;
  biddingId: number | null;
  historicBiddingId: number;
}> = ({ item, onOpenCompany, files, biddingId, historicBiddingId }) => (
  <>
    <div className="item-block mb-4">
      <div className="item-section">
        <div className="item-title">
          Item {item.numero_item ?? '—'}
          {item.numero_lote && item.numero_lote !== '0' ? ` (lote ${item.numero_lote})` : ''} -{' '}
          {item.label}
        </div>
        {item.descricao ? <div>Descrição do item: {item.descricao}</div> : null}
        {item.edital?.quantidade !== null && item.edital?.quantidade !== undefined ? (
          <div>Quantidade no edital: {count(item.edital.quantidade, item.edital.unidade)}</div>
        ) : null}

        {item.edital ? (
          <div>
            Estimado no edital: {money(item.edital.valor_total)}
            {item.edital.valor_unitario
              ? ` (${money(item.edital.valor_unitario)} por unidade)`
              : ''}
            {item.economia !== null ? (
              <span className={item.economia >= 0 ? 'text-success ml-2' : 'text-danger ml-2'}>
                {item.economia >= 0
                  ? `${percent(item.economia)} abaixo do estimado`
                  : `${percent(Math.abs(item.economia))} acima do estimado`}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {item.vencedores.map((winner) => (
        <div key={`${winner.participante}-${winner.valor}`} className="item-section">
          <div className="item-title">
            Informações do item contratado - {winner.participante ?? '—'}
          </div>
          {winner.marca ? <div>Marca: {winner.marca}</div> : null}
          {winner.modelo ? <div>Modelo: {winner.modelo}</div> : null}
          <div>Quantidade de itens: {count(winner.quantidade)}</div>
          <div>
            Valor Negociado: {money(winner.valor)}
            {unitPrice(winner.valor, winner.quantidade) !== null
              ? ` - ${money(unitPrice(winner.valor, winner.quantidade))} por item`
              : ''}
            {winner.reducao_lance
              ? ` - Redução de ${percent(winner.reducao_lance)} do lance final`
              : ''}
          </div>
        </div>
      ))}
    </div>

    <ParticipantsTable
      participants={item.participantes}
      onOpen={onOpenCompany}
      files={files}
      biddingId={biddingId}
      historicBiddingId={historicBiddingId}
    />

    <RejectionsTable participants={item.participantes} onOpen={onOpenCompany} />
  </>
);

const AiManagementHiringHistoryDrawer: React.FC<AiManagementHiringHistoryDrawerProps> = ({
  biddingId,
  historicBiddingId,
  itemKey,
  notice,
  onClose,
}) => {
  const { detail, loading, error } = useHiringHistoryDetail(biddingId, historicBiddingId, itemKey);
  const files = useParticipantFiles(biddingId, historicBiddingId);
  const [company, setCompany] = useState<{ cnpj: string; name: string | null } | null>(null);

  const openCompany = (participant: HiringHistoryParticipant) => {
    if (!participant.cnpj) return;
    setCompany({ cnpj: participant.cnpj.replace(/\D/g, ''), name: participant.participante });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (company) setCompany(null);
      else onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, company]);

  const bidding = detail?.licitacao;
  const items = detail?.itens ?? [];
  const [itemIndex, setItemIndex] = useState(0);
  const currentItem = items[itemIndex] ?? items[0];

  useEffect(() => setItemIndex(0), [detail]);

  const itemOptionLabel = (item: HiringHistoryDrawerItem) =>
    `Item ${item.numero_item ?? '—'}${
      item.numero_lote && item.numero_lote !== '0' ? ` (lote ${item.numero_lote})` : ''
    } - ${item.label ?? ''}`;

  return (
    <>
      <AiManagementDrawer
        title={bidding?.edital ?? notice ?? 'Licitação'}
        shifted={Boolean(company)}
        onClose={onClose}
      >
        <Content>
          {loading ? (
            <div className="py-5">
              <Loader />
            </div>
          ) : null}

          {!loading && error ? (
            <div className="alert alert-warning mb-0" role="alert">
              Não foi possível carregar os dados desta licitação.
            </div>
          ) : null}

          {!loading && !error && bidding ? (
            <>
              <div className="meta-section">
                <div className="meta-label">Objeto</div>
                <div className="meta-value objeto-text">{bidding.objeto ?? '—'}</div>
              </div>

              <div className="row meta-section pb-0">
                <Meta label="Data">{dateBr(bidding.data)}</Meta>
                <Meta label="Nº ConLicitação">
                  <span className="text-primary">{bidding.id}</span>
                </Meta>
                <Meta label="Situação">{bidding.situacao ?? '—'}</Meta>
                <Meta label="Edital">{bidding.edital ?? '—'}</Meta>
                <Meta label="Processo">{bidding.processo ?? '—'}</Meta>
              </div>

              <div className="row meta-section pb-0 mb-4">
                <Meta label="Órgão" className="col-4">
                  <span className="text-muted">{bidding.orgao ?? '—'}</span>
                </Meta>
                <Meta label="UASG" className="col-2">
                  {bidding.uasg ?? '—'}
                </Meta>
                <Meta label="Cidade" className="col-3">
                  {bidding.cidade ?? '—'}
                </Meta>
                <Meta label="Endereço" className="col-3">
                  {bidding.endereco ?? '—'}
                </Meta>
              </div>

              {items.length > 1 ? (
                <div className="form-group mb-3">
                  <label className="meta-label" htmlFor="hiring-history-drawer-item">
                    {items.length} itens desta licitação correspondem a este produto
                  </label>
                  <select
                    id="hiring-history-drawer-item"
                    className="custom-select"
                    value={itemIndex}
                    onChange={(event) => setItemIndex(Number(event.target.value))}
                  >
                    {items.map((item, index) => (
                      <option key={`${item.numero_lote}-${item.numero_item}`} value={index}>
                        {itemOptionLabel(item)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {currentItem ? (
                <ItemBlock
                  item={currentItem}
                  onOpenCompany={openCompany}
                  files={files}
                  biddingId={biddingId}
                  historicBiddingId={historicBiddingId}
                />
              ) : (
                <div className="text-muted">Nenhum item encontrado nesta licitação.</div>
              )}

              <AiManagementOcorrenciasTable
                biddingId={biddingId}
                historicBiddingId={historicBiddingId}
                wrap={(content) => (
                  <div className="participants-table mb-4">
                    <div className="rejections-title">Ocorrências</div>
                    {content}
                  </div>
                )}
              />
            </>
          ) : null}

          {!loading && !error && !bidding ? (
            <div className="text-muted">Nenhum dado encontrado para esta licitação.</div>
          ) : null}
        </Content>
      </AiManagementDrawer>

      {company ? (
        <AiManagementCompanyDrawer
          cnpj={company.cnpj}
          name={company.name}
          onClose={() => setCompany(null)}
        />
      ) : null}
    </>
  );
};

export default AiManagementHiringHistoryDrawer;
